import React, { useEffect, useMemo, useState } from 'react';
import { fetchProducts } from '../../lib/api';
import PageHero from '../../components/ui-shree/PageHero';
import OrderModal from '../Sevagram/OrderModal';
import CostReceiptModal from '../../components/Shop/CostReceiptModal';
import SynergyPanel from '../../components/Shop/SynergyPanel';

const FORMULA_DRIVEN_SLUGS = new Set([
  'shreesavaa-signature-ritual',
  'shreesavaa-radiance-lotion',
  'shreesavaa-root-to-tip-conditioner',
]);

// Products that participate in the Onion Seed × Rice Bran × Sunflower
// Lecithin "Protocol of Harmony". The Conditioner is the canonical case.
const SYNERGY_SLUGS = new Set([
  'shreesavaa-root-to-tip-conditioner',
]);

const CATEGORIES = ['All', 'serum', 'oil', 'cream', 'mist', 'ritual'];

const ShopPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');
  const [orderSlug, setOrderSlug] = useState(null);

  useEffect(() => {
    fetchProducts()
      .then(setProducts)
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(
    () => (filter === 'All' ? products : products.filter((p) => p.category === filter)),
    [filter, products]
  );

  return (
    <div data-testid="shop-page">
      <PageHero
        overline="The Apothecary"
        title={<>The library of <em className="text-[#90B0BF]">rituals</em>.</>}
        subtitle="Each formula composed in small batches, hand-numbered, and signed on chain. Choose what your skin is asking for today."
        testId="shop-hero"
      />

      <section className="px-6 md:px-12 pb-32">
        <div className="max-w-[1400px] mx-auto">
          {/* Filters */}
          <div
            data-testid="shop-filters"
            className="flex flex-wrap items-center gap-3 border-b border-[#90B0BF]/30 pb-6 mb-12"
          >
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                data-testid={`filter-${c.toLowerCase()}`}
                className={`px-4 py-2 text-[11px] tracking-[0.22em] uppercase font-medium border transition-colors ${
                  filter === c
                    ? 'border-[#7C9CB1] bg-[#7C9CB1] text-[#F4F8F9]'
                    : 'border-[#C1D7E2] text-[#7C9CB1] hover:bg-[#C1D7E2]/40'
                }`}
              >
                {c === 'All' ? 'All Rituals' : c}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-12">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="aspect-[3/4] bg-[#C1D7E2]/40 animate-pulse" />
              ))}
            </div>
          ) : (
            <div
              data-testid="shop-grid"
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-12 md:gap-16"
            >
              {filtered.map((p, idx) => (
                <article
                  key={p.id}
                  data-testid={`shop-product-${idx}`}
                  className="group"
                >
                  <div className="relative aspect-[3/4] overflow-hidden bg-[#F4F8F9] mb-5">
                    <img
                      src={p.image_url}
                      alt={p.name}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                    />
                    <span className="absolute top-3 left-3 font-mono text-[9px] tracking-[0.3em] uppercase text-[#7C9CB1] bg-[#F4F8F9]/85 backdrop-blur-md px-2 py-1 border border-[#C1D7E2]">
                      {p.category}
                    </span>
                    <div className="absolute bottom-3 right-3 backdrop-blur-md bg-[#F4F8F9]/80 border border-[#C1D7E2] px-3 py-1.5">
                      <span className="font-mono text-[10px] tracking-[0.2em] text-[#7C9CB1]">
                        +{p.token_reward} SEVA
                      </span>
                    </div>
                  </div>

                  <div className="flex items-baseline justify-between gap-4">
                    <h3 className="font-serif text-2xl md:text-3xl text-[#7C9CB1] font-light leading-tight">
                      {p.name}
                    </h3>
                    <p className="font-mono text-base text-[#7C9CB1] whitespace-nowrap">
                      ${p.price_usd.toFixed(0)}
                    </p>
                  </div>
                  <p className="mt-2 text-sm font-light italic text-[#90B0BF]">
                    {p.tagline}
                  </p>
                  <p className="mt-4 text-sm font-light text-[#7C9CB1]/85 leading-relaxed">
                    {p.description}
                  </p>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {p.key_ingredients.map((k) => (
                      <span
                        key={k}
                        className="font-mono text-[10px] tracking-[0.18em] uppercase text-[#7C9CB1] border border-[#C1D7E2] px-2 py-1"
                      >
                        {k}
                      </span>
                    ))}
                  </div>
                  <div className="mt-6 flex items-center justify-between gap-4 border-t border-[#C1D7E2] pt-4">
                    <span className="font-mono text-[10px] tracking-[0.2em] text-[#90B0BF] uppercase">
                      Volume · {p.volume}
                    </span>
                    <div className="flex items-center gap-5">
                      {SYNERGY_SLUGS.has(p.slug) && (
                        <SynergyPanel
                          productName={p.name}
                          testIdSuffix={`-shop-${idx}`}
                        />
                      )}
                      {FORMULA_DRIVEN_SLUGS.has(p.slug) && (
                        <CostReceiptModal
                          slug={p.slug}
                          productName={p.name}
                          testIdSuffix={`-shop-${idx}`}
                        />
                      )}
                      <button
                        onClick={() => setOrderSlug(p.slug)}
                        data-testid={`shop-order-${idx}`}
                        className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#7C9CB1] hover:text-[#90B0BF] transition-colors"
                      >
                        Place order →
                      </button>
                    </div>
                  </div>
                  <p className="mt-3 font-mono text-[10px] tracking-[0.2em] text-[#90B0BF] text-right">
                    {p.blockchain_hash}
                  </p>
                </article>
              ))}
            </div>
          )}

          {!loading && filtered.length === 0 && (
            <p
              data-testid="shop-empty"
              className="text-center text-[#7C9CB1] font-serif italic text-2xl py-20"
            >
              The shelf is quiet here. Try another ritual.
            </p>
          )}
        </div>
      </section>

      {orderSlug && (
        <OrderModal
          initialProductSlugs={[orderSlug]}
          onClose={() => setOrderSlug(null)}
        />
      )}
    </div>
  );
};

export default ShopPage;
